<template>
    <div md="7">
        <BarChart
            :data=chartData
            :options=chartOptions
            :height="250"
        >
        </BarChart>
    </div>
</template>

<script>
import useCommonData from '@/composables/useCommonData'
import { getCurrentInstance, computed, toRefs } from '@vue/composition-api'
import CreateVueChart from '@/components/CreateVueChart.js'
export default {
    components: {
        BarChart: CreateVueChart('Bar')
    },
    props: {
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        },
        cumulativeCost: {
            type: Object,
            default: function () {
                return {
                    currentMonthCost: null,
                    estimatedCost: null,
                    prevMonthCost: null
                }
            }
        },
        cumulativeCostStatus: {
            type: Boolean
        },
        month: {
            type: String,
            default: ''
        },
        prevMonth: {
            type: String,
            default: ''
        },
        estimated: {
            type: Boolean
        },
        status: {
            type: [String, Number],
            default: ''
        }
    },
    setup (props) {
        const vm = getCurrentInstance().proxy
        const yKeys = ['Last Month', 'Current', 'Estimated Cost']
        const { __toKFormat, __enMonthMap } = useCommonData()
        const result = computed(() => {
            const { estimatedCost, prevMonthCost, currentMonthCost } = cumulativeCost.value
            return estimated.value ? estimatedCost - prevMonthCost : currentMonthCost - prevMonthCost
        })
        const { prevMonth, month, cumulativeCost, cumulativeCostStatus, estimated, themeColors } = toRefs(props)
        const getYData = (label) => __toKFormat(label)
        const transparentize = (value, opacity) => {
            const r = parseInt(value.slice(1, 3), 16)
            const g = parseInt(value.slice(3, 5), 16)
            const b = parseInt(value.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        }
        const prevMonthCost = computed(() => cumulativeCost.value.prevMonthCost)
        const currentMonthCost = computed(() => cumulativeCost.value.currentMonthCost)
        const estimatedCost = computed(() => cumulativeCost.value.estimatedCost)

        const datasetArr = computed(() => [
            {
                type: 'bar',
                label: yKeys[0],
                data: [ prevMonthCost.value ],
                stack: 'cost',
                barPercentage: 0.75,
                backgroundColor: themeColors.value.secondary,
                hoverBackgroundColor: transparentize(themeColors.value.secondary, 0.8)
            },
            {
                type: 'bar',
                label: yKeys[1],
                data: [ null, currentMonthCost.value ],
                stack: 'cost',
                barPercentage: 0.75,
                backgroundColor: themeColors.value.primary,
                hoverBackgroundColor: transparentize(themeColors.value.primary, 0.8)
            },
            {
                type: 'bar',
                label: yKeys[2],
                data: [ null, (estimatedCost.value || 0) - (currentMonthCost.value || 0) ],
                stack: 'cost',
                barPercentage: 0.75,
                backgroundColor: '#d1cdfa',
                hoverBackgroundColor: transparentize('#d1cdfa', 0.8)
            }
        ])

        const chartData = computed(() => {
            return {
                labels: [prevMonth.value.substring(2, 7), month.value.substring(2, 7)],
                datasets: estimated.value && cumulativeCostStatus.value ? datasetArr.value : datasetArr.value.slice(0, 2)
            }
        })

        const chartOptions = computed(() => {
            return {
                layout: {
                    padding: {
                        top: 20,
                        bottom: 0,
                        left: 20,
                        right: 0
                    }
                },
                annotation: {
                    annotations: [
                        {
                            type: 'line',
                            id: 'line-1',
                            mode: 'horizontal',
                            scaleID: 'y-axis-0',
                            value: cumulativeCost.value.currentMonthCost,
                            borderColor: estimated.value && cumulativeCostStatus.value ? themeColors.value.danger : 'transparent',
                            borderWidth: 2,
                            borderDash: [3]
                        }
                    ]
                },
                plugins: {
                    datalabels: {
                        display: false
                    }
                },
                animation: false,
                responsive: true,
                maintainAspectRatio: false,
                legend: {
                    display: false
                },
                tooltips: {
                    callbacks: {
                        title: tooltipItem => yKeys[tooltipItem[0].datasetIndex],
                        label: tooltipItem => ` ${vm.$_getDollar(tooltipItem.value)}`
                    }
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                display: true,
                                callback: value => value
                            }
                        }
                    ],
                    yAxes: [
                        {
                            drawOnChartArea: 100,
                            stacked: true,
                            display: true,
                            gridLines: {
                                zeroLineColor: 'rgba(200, 200, 200, 0.65)',
                                color: 'transparent'
                            },
                            ticks: {
                                callback: getYData,
                                autoSkip: false,
                                maxTicksLimit: 5
                            }
                        }
                    ]
                },
                onHover: (event, chartElement) => { event.target.style.cursor = chartElement.length ? 'pointer' : 'default' }
            }
        })

        return {
            chartData,
            chartOptions,
            __enMonthMap,
            result,
            __toKFormat,
            yKeys
        }
    }
}
</script>

<style>

</style>
